/**
 * BOSS Reports — which row fields are frozen at the left, and which scroll with the data.
 *
 * Every row field stays in `group_by`, always. That is what makes the rows what they are — one per
 * distinct combination of ALL row fields — and it is why pinning can never merge rows, re-sum a
 * measure, or turn a description into a "value". Pinning is purely a question of where the
 * datagrid DRAWS a row field: in the frozen block on the left, or as a scrolling column before the
 * measures. The drawing is done by lib/bossRowPinning; this module owns the state.
 *
 * The state is two lists, persisted in the layout envelope beside `viewer` (never inside it —
 * Perspective's `save()` drops anything it does not know, so a marker in the viewer config would
 * vanish on the first user edit):
 *
 *   pinnedRowFields    the row fields drawn in the frozen block
 *   unpinnedRowFields  the row fields drawn as scrolling columns
 *
 * They record MEMBERSHIP, not order. The order the columns are DRAWN in is the Rows area's own
 * order — `group_by` — and `normaliseRowPins` is the one place the two are reconciled. That is what
 * keeps the grid's columns and the field bar's Rows chips saying the same thing, and they drifted
 * for as long as the lists carried an order of their own: pinning appended, so a field dropped at
 * the FRONT of Rows was still drawn last, and every consumer of the frozen block indexes the drawn
 * order — the captions, the corner sort, the right-click hit test, the width carry-over — so the
 * mismatch did not merely look wrong, it named the wrong field.
 *
 * The frozen block is necessarily the LEFTMOST columns: regular-table freezes row headers 0..P-1
 * and lib/bossRowPinning re-emits the unpinned fields as data columns after them. So unpinning does
 * move a field right, past the fields still frozen. Within each block, Rows order is exact.
 *
 * Absence means "everything pinned, in Rows order" — which is what every layout saved before this
 * existed looks like, and what the legacy desktop grid drew — so nothing needs migrating.
 */
import type { ViewerConfig } from './bossLayoutOps'

export type BossRowPins = {
    /** The frozen block; in Rows order once normalised. */
    pinned: string[]
    /** The scrolling row fields, drawn before the measures; in Rows order once normalised. */
    unpinned: string[]
}

/** The two envelope keys, as the server validates and stores them. */
export const PINNED_KEY = 'pinnedRowFields'
export const UNPINNED_KEY = 'unpinnedRowFields'

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []

/** The Rows area, as names. */
export function rowFieldsOf(config: ViewerConfig): string[] {
    return strings(config.group_by)
}

/**
 * Reconciles a stored (or edited) pin state with the config it now applies to.
 *
 * The result is `group_by` partitioned in two, IN ROWS ORDER — the whole reason this is done in one
 * place. The stored lists decide only which side of the partition a field falls on:
 *
 *  - a name that is not in Rows is dropped: a field dragged out of Rows is neither pinned nor
 *    unpinned, it is gone;
 *  - a row field named in neither list is PINNED, which is how a field dragged into Rows starts
 *    frozen without the drag having to know pins exist;
 *  - a name in both lists is pinned, which is how the old first-appearance rule resolved it too.
 *
 * The ORDER comes from `group_by` and never from the lists. Reordering a chip in the field bar
 * rewrites `group_by` and nothing else, so anything that took its order from a stored list simply
 * stayed where it was — the columns and the Rows chips then disagreed, and every index into the
 * frozen block named a field one place out.
 */
export function normaliseRowPins(
    config: ViewerConfig,
    pins: Partial<BossRowPins> | null | undefined,
): BossRowPins {
    const isPinned = new Set(strings(pins?.pinned))
    const isUnpinned = new Set(strings(pins?.unpinned))
    const pinned: string[] = []
    const unpinned: string[] = []
    const seen = new Set<string>()
    // A duplicate in `group_by` is not a shape the config should ever carry, but a hand-edited
    // layout can: it is drawn once, at its first position, so the partition still lines up with the
    // one column the grid draws for it.
    for (const name of rowFieldsOf(config)) {
        if (seen.has(name)) continue
        seen.add(name)
        if (isUnpinned.has(name) && !isPinned.has(name)) unpinned.push(name)
        else pinned.push(name)
    }
    return { pinned, unpinned }
}

/** Reads the two lists off a stored layout envelope, in whatever shape it came back. */
export function rowPinsOfEnvelope(envelope: unknown): Partial<BossRowPins> | null {
    if (!isRecord(envelope)) return null
    const pinned = envelope[PINNED_KEY]
    const unpinned = envelope[UNPINNED_KEY]
    if (pinned === undefined && unpinned === undefined) return null
    return { pinned: strings(pinned), unpinned: strings(unpinned) }
}

export function sameRowPins(a: BossRowPins, b: BossRowPins): boolean {
    return a.pinned.length === b.pinned.length
        && a.unpinned.length === b.unpinned.length
        && a.pinned.every((name, i) => name === b.pinned[i])
        && a.unpinned.every((name, i) => name === b.unpinned[i])
}

/** True when the field is drawn in the frozen block. A field not in Rows is never pinned. */
export function isRowFieldPinned(pins: BossRowPins, name: string): boolean {
    return pins.pinned.includes(name)
}

/**
 * Pins a row field: out of the scrolling block and into the frozen one.
 *
 * WHERE it lands is not this function's business. `normaliseRowPins` draws the frozen block in Rows
 * order, so the field takes the position its Rows chip already has; the append below only keeps the
 * list a list.
 *
 * Returns the same object when the field is already pinned or is not a row field at all, so a
 * caller comparing identity can tell a real change from a click that changed nothing.
 */
export function pinRowField(pins: BossRowPins, name: string): BossRowPins {
    if (pins.pinned.includes(name) || !pins.unpinned.includes(name)) return pins
    return {
        pinned: [...pins.pinned, name],
        unpinned: pins.unpinned.filter(entry => entry !== name),
    }
}

/**
 * Unpins a row field: out of the frozen block and into the scrolling one, where `normaliseRowPins`
 * draws it at its Rows position among the other scrolling fields — after everything still frozen,
 * because the frozen block is necessarily the leftmost columns.
 *
 * The frozen block may empty out entirely — a report with nothing held in place is a legitimate
 * choice, and the grid draws it as ordinary scrolling columns.
 */
export function unpinRowField(pins: BossRowPins, name: string): BossRowPins {
    if (pins.unpinned.includes(name) || !pins.pinned.includes(name)) return pins
    return {
        pinned: pins.pinned.filter(entry => entry !== name),
        unpinned: [name, ...pins.unpinned],
    }
}
